import { createStore } from '@xstate/store';
import type { BoardDefinition } from '../types';

// ============================================
// Boards Store - Verwaltet die Boards
// ============================================

const BOARDS_LOCAL_STORAGE_KEY = 'roadmap_2026_boards';
const PERSISTENCE_DEBOUNCE_MS = 500;

export const DEFAULT_BOARD_ID = 'default';

export interface BoardsState {
  boards: BoardDefinition[];
  activeBoardId: string | null;
}

// ============================================
// Persistence
// ============================================

function loadBoardsFromLocalStorage(): BoardsState {
  try {
    const saved = localStorage.getItem(BOARDS_LOCAL_STORAGE_KEY);
    if (!saved) {
      return { boards: [], activeBoardId: null };
    }

    const parsed = JSON.parse(saved) as BoardsState;
    if (!Array.isArray(parsed.boards)) {
      return { boards: [], activeBoardId: null };
    }
    return parsed;
  } catch {
    return { boards: [], activeBoardId: null };
  }
}

function saveBoardsToLocalStorage(state: BoardsState): void {
  localStorage.setItem(BOARDS_LOCAL_STORAGE_KEY, JSON.stringify(state));
}

// ============================================
// Store
// ============================================

const initialState = loadBoardsFromLocalStorage();

export const boardsStore = createStore({
  context: initialState,

  on: {
    'board.create': (ctx, event: { board: BoardDefinition }) => {
      if (ctx.boards.some(b => b.id === event.board.id)) {
        console.warn(`[boardsStore] Board with ID "${event.board.id}" already exists`);
        return ctx;
      }
      const newBoards = [...ctx.boards, event.board];
      return {
        ...ctx,
        boards: newBoards,
        // Wenn kein aktives Board, das neue aktivieren
        activeBoardId: ctx.activeBoardId ?? event.board.id,
      };
    },

    'board.update': (ctx, event: { boardId: string; updates: Partial<Omit<BoardDefinition, 'id' | 'createdAt'>> }) => {
      const boardIndex = ctx.boards.findIndex(b => b.id === event.boardId);
      if (boardIndex === -1) {
        console.warn(`[boardsStore] Board with ID "${event.boardId}" not found`);
        return ctx;
      }

      const updatedBoards = [...ctx.boards];
      updatedBoards[boardIndex] = {
        ...updatedBoards[boardIndex],
        ...event.updates,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...ctx,
        boards: updatedBoards,
      };
    },

    'board.delete': (ctx, event: { boardId: string }) => {
      const boardIndex = ctx.boards.findIndex(b => b.id === event.boardId);
      if (boardIndex === -1) {
        console.warn(`[boardsStore] Board with ID "${event.boardId}" not found`);
        return ctx;
      }

      const newBoards = ctx.boards.filter(b => b.id !== event.boardId);

      // Board-spezifische localStorage Daten löschen
      localStorage.removeItem(`roadmap_2026_board_${event.boardId}_tasks`);
      localStorage.removeItem(`roadmap_2026_board_${event.boardId}_columns`);
      localStorage.removeItem(`roadmap_2026_board_${event.boardId}_requirements`);
      localStorage.removeItem(`roadmap_2026_board_${event.boardId}_task_views`);
      localStorage.removeItem(`roadmap_2026_board_${event.boardId}_canvas`);

      // Wenn aktives Board geloescht, zum ersten verbleibenden wechseln
      const newActiveBoardId = ctx.activeBoardId === event.boardId
        ? (newBoards[0]?.id ?? null)
        : ctx.activeBoardId;

      return {
        ...ctx,
        boards: newBoards,
        activeBoardId: newActiveBoardId,
      };
    },

    'board.setActive': (ctx, event: { boardId: string }) => {
      if (!ctx.boards.some(b => b.id === event.boardId)) {
        console.warn(`[boardsStore] Board with ID "${event.boardId}" not found`);
        return ctx;
      }
      return {
        ...ctx,
        activeBoardId: event.boardId,
      };
    },

    'boards.import': (ctx, event: { boards: BoardDefinition[] }) => ({
      ...ctx,
      boards: event.boards,
      activeBoardId: event.boards[0]?.id ?? null,
    }),
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastBoardsHash = '';

function computeBoardsHash(state: BoardsState): string {
  const boardsHash = state.boards.map(b =>
    `${b.id}:${b.name}:${b.startDate}:${b.employeeIds.join(',')}`
  ).join('|');
  return `${boardsHash}::${state.activeBoardId}`;
}

boardsStore.subscribe((snapshot) => {
  const currentHash = computeBoardsHash(snapshot.context);

  if (currentHash === lastBoardsHash) return;
  lastBoardsHash = currentHash;

  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveBoardsToLocalStorage(snapshot.context);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getBoardsArray(): BoardDefinition[] {
  return boardsStore.getSnapshot().context.boards;
}

export function getBoardById(boardId: string): BoardDefinition | undefined {
  return boardsStore.getSnapshot().context.boards.find(b => b.id === boardId);
}

export function getActiveBoardId(): string | null {
  return boardsStore.getSnapshot().context.activeBoardId;
}

export function getActiveBoard(): BoardDefinition | undefined {
  const activeBoardId = getActiveBoardId();
  if (!activeBoardId) return undefined;
  return getBoardById(activeBoardId);
}

export function generateBoardId(baseName: string): string {
  const baseId = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const existingBoards = getBoardsArray();
  let id = baseId || 'new-board';
  let counter = 1;

  while (existingBoards.some(b => b.id === id)) {
    id = `${baseId}-${counter}`;
    counter++;
  }

  return id;
}

export type BoardsStore = typeof boardsStore;
